import { useMemo, useCallback, useState } from 'react';
import { Extension, StateEffect, StateField } from '@codemirror/state';
import { EditorView, keymap } from '@codemirror/view';

type ExtractDialogState = {
  visible: boolean;
  type: 'function' | 'variable' | null;
  selectedCode: string;
  from: number;
  to: number;
};

// State effects
const showExtractDialog = StateEffect.define<Omit<ExtractDialogState, 'visible'>>();
const hideExtractDialog = StateEffect.define<null>();

// State field
const extractDialogState = StateField.define<ExtractDialogState>({
  create: () => ({ visible: false, type: null, selectedCode: '', from: 0, to: 0 }),
  update(value, tr) {
    for (const effect of tr.effects) {
      if (effect.is(showExtractDialog)) {
        return { ...effect.value, visible: true };
      }
      if (effect.is(hideExtractDialog)) {
        return { ...value, visible: false };
      }
    }
    return value;
  },
});

// Find variables used in the selected code that are defined outside
function findExternalVariables(code: string, selectedCode: string, from: number): string[] {
  const variables = new Set<string>();

  // Find all identifiers used in the selection
  const identifierRegex = /\b([a-zA-Z_$][a-zA-Z0-9_$]*)\b/g;
  let match;
  while ((match = identifierRegex.exec(selectedCode)) !== null) {
    const name = match[1];
    // Skip keywords and common globals
    const keywords = [
      'const', 'let', 'var', 'function', 'return', 'if', 'else', 'for', 'while',
      'switch', 'case', 'break', 'continue', 'try', 'catch', 'throw', 'new',
      'this', 'true', 'false', 'null', 'undefined', 'typeof', 'instanceof',
      'async', 'await', 'class', 'extends', 'import', 'export', 'default',
      'console', 'window', 'document', 'Array', 'Object', 'String', 'Number',
      'Boolean', 'Math', 'JSON', 'Promise', 'Date', 'Error', 'Map', 'Set',
      'React', 'useState', 'useEffect', 'useCallback', 'useMemo', 'useRef',
    ];
    if (keywords.includes(name)) continue;

    // Check if the variable is defined within the selection
    const definedInSelection = new RegExp(
      `(?:const|let|var|function)\\s+${name}\\b`
    ).test(selectedCode);

    if (!definedInSelection) {
      // Check if it's defined before the selection
      const codeBefore = code.slice(0, from);
      const definedBefore = new RegExp(
        `(?:const|let|var|function)\\s+${name}\\b`
      ).test(codeBefore);

      if (definedBefore) {
        variables.add(name);
      }
    }
  }

  return Array.from(variables);
}

// Detect if the selection returns a value (for variable extraction)
function detectsReturnValue(selectedCode: string): boolean {
  const trimmed = selectedCode.trim();

  // If it's a single expression (no semicolon or only at end)
  if (!trimmed.includes(';') || trimmed.endsWith(';')) {
    // Check if it looks like an expression
    const expressionPatterns = [
      /^[\w$]+\s*\(/, // Function call
      /^[\w$]+\./, // Method chain
      /^\[.*\]$/, // Array literal
      /^\{.*\}$/, // Object literal
      /^`.*`$/, // Template literal
      /^['"].*['"]$/, // String literal
      /^\d+/, // Number
      /^[\w$]+$/, // Single identifier
      /^[\w$]+\s*[+\-*/%]/, // Arithmetic expression
      /^[\w$]+\s*[<>=!]/, // Comparison expression
      /^[\w$]+\s*\?/, // Ternary expression
    ];

    return expressionPatterns.some((p) => p.test(trimmed.replace(/;$/, '')));
  }

  return false;
}

// Find a good insertion point (before the current function/component)
function findInsertionPoint(code: string, selectionFrom: number): number {
  // Find the start of the current function/class/component
  const before = code.slice(0, selectionFrom);

  // Look for function/const/class definitions
  const patterns = [
    /(?:export\s+)?(?:async\s+)?function\s+\w+\s*\([^)]*\)\s*\{[^]*$/,
    /(?:export\s+)?const\s+\w+\s*=\s*(?:async\s*)?\([^)]*\)\s*=>\s*\{[^]*$/,
    /(?:export\s+)?const\s+\w+\s*=\s*(?:async\s*)?\([^)]*\)\s*=>[^]*$/,
    /(?:export\s+)?class\s+\w+[^]*$/,
  ];

  let earliestMatch = selectionFrom;
  for (const pattern of patterns) {
    const match = before.match(pattern);
    if (match) {
      const start = selectionFrom - match[0].length;
      if (start < earliestMatch) {
        earliestMatch = start;
      }
    }
  }

  return earliestMatch;
}

// Generate extracted function
function generateExtractedFunction(
  name: string,
  selectedCode: string,
  params: string[],
  returnsValue: boolean
): string {
  const paramStr = params.join(', ');
  const indent = '  ';
  const lines = selectedCode.split('\n').map((line) => indent + line);
  const body = lines.join('\n');

  if (returnsValue) {
    const cleanBody = selectedCode.trim().replace(/;$/, '');
    return `function ${name}(${paramStr}) {\n  return ${cleanBody};\n}\n\n`;
  }

  return `function ${name}(${paramStr}) {\n${body}\n}\n\n`;
}

// Generate function call
function generateFunctionCall(name: string, params: string[], returnsValue: boolean): string {
  const call = `${name}(${params.join(', ')})`;
  return returnsValue ? call : `${call};`;
}

// Extract to function command
function extractToFunction(view: EditorView): boolean {
  const selection = view.state.selection.main;
  if (selection.empty || selection.to - selection.from < 3) {
    return false;
  }

  const selectedCode = view.state.sliceDoc(selection.from, selection.to);

  view.dispatch({
    effects: showExtractDialog.of({
      type: 'function',
      selectedCode,
      from: selection.from,
      to: selection.to,
    }),
  });

  return true;
}

// Extract to variable command
function extractToVariable(view: EditorView): boolean {
  const selection = view.state.selection.main;
  if (selection.empty || selection.to - selection.from < 2) {
    return false;
  }

  const selectedCode = view.state.sliceDoc(selection.from, selection.to);

  // Check if selection is a valid expression
  if (!detectsReturnValue(selectedCode)) {
    return false;
  }

  view.dispatch({
    effects: showExtractDialog.of({
      type: 'variable',
      selectedCode,
      from: selection.from,
      to: selection.to,
    }),
  });

  return true;
}

export function useExtractRefactor() {
  const [extractName, setExtractName] = useState('');
  const [dialogState, setDialogState] = useState<ExtractDialogState>({
    visible: false,
    type: null,
    selectedCode: '',
    from: 0,
    to: 0,
  });

  const performExtraction = useCallback(
    (view: EditorView, name: string, state: ExtractDialogState) => {
      if (!name || !state.type) return;

      const code = view.state.doc.toString();

      if (state.type === 'function') {
        const params = findExternalVariables(code, state.selectedCode, state.from);
        const returnsValue = detectsReturnValue(state.selectedCode);
        const insertPoint = findInsertionPoint(code, state.from);

        const newFunction = generateExtractedFunction(name, state.selectedCode, params, returnsValue);
        const functionCall = generateFunctionCall(name, params, returnsValue);

        view.dispatch({
          changes: [
            { from: insertPoint, to: insertPoint, insert: newFunction },
            { from: state.from + newFunction.length, to: state.to + newFunction.length, insert: functionCall },
          ],
        });
      } else if (state.type === 'variable') {
        // Find the line start for variable declaration
        const line = view.state.doc.lineAt(state.from);
        const indent = line.text.match(/^(\s*)/)?.[1] || '';

        const cleanCode = state.selectedCode.trim().replace(/;$/, '');
        const declaration = `${indent}const ${name} = ${cleanCode};\n`;

        view.dispatch({
          changes: [
            { from: line.from, to: line.from, insert: declaration },
            { from: state.from + declaration.length, to: state.to + declaration.length, insert: name },
          ],
        });
      }

      // Hide dialog
      view.dispatch({ effects: hideExtractDialog.of(null) });
      setDialogState((prev) => ({ ...prev, visible: false }));
      setExtractName('');
    },
    []
  );

  const extractRefactorExtension = useMemo<Extension>(() => {
    return [
      extractDialogState,
      keymap.of([
        {
          // Cmd+Shift+M for extract to function
          key: 'Mod-Shift-m',
          run: extractToFunction,
        },
        {
          // Cmd+Shift+V for extract to variable (Alt to avoid conflict)
          key: 'Alt-Shift-v',
          run: extractToVariable,
        },
      ]),
      EditorView.updateListener.of((update) => {
        const state = update.state.field(extractDialogState);
        setDialogState(state);
      }),
    ];
  }, []);

  return {
    extractRefactorExtension,
    extractDialogState: dialogState,
    extractName,
    setExtractName,
    performExtraction,
    hideDialog: useCallback((view: EditorView) => {
      view.dispatch({ effects: hideExtractDialog.of(null) });
      setDialogState((prev) => ({ ...prev, visible: false }));
      setExtractName('');
    }, []),
  };
}

// Export commands for command palette
export const extractCommands = [
  { name: 'Extract to Function', fn: extractToFunction },
  { name: 'Extract to Variable', fn: extractToVariable },
];
